import { useEffect, useMemo, useState } from 'react';
import { api, AppSetting, HubspotPipelineLite } from '../lib/api';
import {
  SectionLabel, Card, Badge, ActionButton, Spinner, EmptyState,
  Table, Th, Tr, Td,
} from '../components/ui';

// ─── Settings ───────────────────────────────────────────────────────────────
// App-wide tunables that ops/finance need to change without shipping code.
// Each setting has a known type; validation and rendering live in this
// file's KNOWN_SETTINGS map so adding a new tunable is a one-entry change.

interface KnownSetting {
  key: string;
  label: string;
  help: string;
  /** HTML input type. Currently only 'date', 'text', and 'number' are used. */
  input: 'date' | 'text' | 'number';
  /** Client-side validator. Return null if valid, or an error string. */
  validate: (value: string) => string | null;
  /** Optional placeholder / default hint shown when the setting is unset. */
  defaultHint?: string;
}

const KNOWN_SETTINGS: KnownSetting[] = [
  {
    key: 'overhead_inclusion_date',
    label: 'Overhead inclusion date',
    help: 'Overheads that shipped before this date are excluded from Shipped Value on every bonus calculation. Use the start date of the bonus period that first included overheads.',
    input: 'date',
    validate: (v) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? null : 'Must be YYYY-MM-DD'),
  },
  {
    key: 'fpa_working_hours_per_month',
    label: 'FP&A — standard working hours per month',
    help: 'Capacity hours per person per month used when seeding a budget from actuals. It anchors the capacity.hours_per_head driver (and per-seat target hours) to true capacity, and utilisation is then derived as billable ÷ capacity — the meaningful billable-of-capacity rate — instead of the logged-hours basis that under-counts un-logged non-billable time. Revenue is unaffected (utilisation rescales to match). Default 160 (≈ 8h × 20 working days); use e.g. 150 net of PTO/holidays or 173.33 (2080/12) for a full-year average.',
    input: 'number',
    defaultHint: '160',
    validate: (v) => {
      const n = parseFloat(v);
      if (!Number.isFinite(n) || n <= 0) return 'Must be a positive number';
      if (n > 744) return 'Must be ≤ 744 (hours in a month)';
      return null;
    },
  },
  {
    key: 'onboarding_go_live_date',
    label: 'Deal onboarding — go-live cutoff date',
    help: 'Deals closed BEFORE this date are treated as historical: hidden from the default onboarding queue (visible via the “Historical” toggle), so pre-launch deals already handled by hand don’t clutter the list. Default 2026-06-22.',
    input: 'date',
    defaultHint: '2026-06-22',
    validate: (v) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? null : 'Must be YYYY-MM-DD'),
  },
  {
    key: 'billing_min_sync_recency_minutes',
    label: 'Billing — minimum sync recency (minutes)',
    help: 'Blocks the billing import from running if the last successful FULL data sync is older than this. Incremental syncs (the hourly job) do NOT count — they only re-fetch issues Jira marked as updated and can miss worklog changes against unchanged deliverables. The relevant pivot is the daily 03:30 UTC full sync (or a manually-triggered sync from Data sync). Default 60. In practice billing runs are always for the previous week or earlier, so this gate is a safety net against stale data after a sync outage.',
    input: 'number',
    defaultHint: '60',
    validate: (v) => {
      const n = parseInt(v, 10);
      if (!Number.isFinite(n) || n <= 0) return 'Must be a positive integer';
      if (n > 24 * 60) return 'Must be ≤ 1440 (24h)';
      return null;
    },
  },
  ...onboardingSla(),
];

/** Deal-onboarding SLA targets (calendar days). One entry per transition. */
function onboardingSla(): KnownSetting[] {
  const days = (v: string): string | null => {
    const n = parseInt(v, 10);
    if (!Number.isFinite(n) || n < 0 || String(n) !== v) return 'Must be a whole number of days';
    if (n > 365) return 'Must be ≤ 365';
    return null;
  };
  const mk = (key: string, label: string, help: string, hint: string): KnownSetting => ({
    key, label, help, input: 'number', defaultHint: hint, validate: days,
  });
  // Every step is measured independently from the close date (no order); invoice
  // paid is judged against the invoice's own due date, with a fallback here.
  return [
    mk('onboarding_sla_closed_to_issued_days', 'Onboarding SLA — closed won → invoice issued', 'Target calendar days from a deal closing to its invoice being issued/sent.', '3'),
    mk('onboarding_sla_invoice_paid_fallback_days', 'Onboarding SLA — invoice paid (fallback days)', 'Invoice-paid is normally judged against the invoice’s own due date; this fallback (days from close) is used only when the invoice has no due date.', '30'),
    mk('onboarding_sla_closed_to_engagement_days', 'Onboarding SLA — closed won → engagement created', 'Target calendar days from closing to the Jira engagement being created.', '5'),
    mk('onboarding_sla_closed_to_sa_days', 'Onboarding SLA — closed won → SA assigned', 'Target calendar days from closing to a Solution Architect being assigned on the Jira engagement. Assignment is stamped when the sync first sees the field filled (Jira doesn’t record when it was set). Only applies to engagement types that require an SA (see the required-steps matrix).', '5'),
    mk('onboarding_sla_closed_to_dm_days', 'Onboarding SLA — closed won → DM assigned', 'Target calendar days from closing to a Delivery Manager (or Client Enablement Lead) being assigned on the Jira engagement. Assignment is stamped when the sync first sees the field filled. Only applies to engagement types that require a DM (see the required-steps matrix).', '5'),
    mk('onboarding_sla_closed_to_handoff_days', 'Onboarding SLA — closed won → handoff doc received', 'Target calendar days from closing to the sales→service handoff document being received.', '5'),
    mk('onboarding_sla_closed_to_welcome_days', 'Onboarding SLA — closed won → welcome email sent', 'Target calendar days from closing to the welcome email going out.', '7'),
    mk('onboarding_sla_closed_to_prekickoff_days', 'Onboarding SLA — closed won → pre-kickoff scheduled', 'Target calendar days from closing to a pre-kickoff being scheduled.', '10'),
    mk('onboarding_sla_closed_to_kickoff_days', 'Onboarding SLA — closed won → kickoff scheduled', 'Target calendar days from closing to the kickoff being scheduled.', '14'),
    mk('onboarding_sla_due_soon_buffer_days', 'Onboarding SLA — “due soon” buffer (days)', 'A stage flags amber “due soon” when it’s within this many days of breaching its target.', '1'),
    {
      key: 'onboarding_auto_archive_days',
      label: 'Deal onboarding — auto-archive after (days)',
      help: 'Days a completed or dismissed record stays in the default onboarding queue before it auto-archives (hidden unless the “Archived” toggle on the queue is on). Records whose checklist is fully done are auto-marked complete by the hourly refresh, so finished deals clear out on their own. 0 = archive immediately. Default 1.',
      input: 'number',
      defaultHint: '1',
      validate: (v) => {
        const n = parseInt(v, 10);
        if (!Number.isFinite(n) || n < 0 || String(n) !== v) return 'Must be a whole number of days';
        if (n > 365) return 'Must be ≤ 365';
        return null;
      },
    },
    {
      key: 'onboarding_digest_hour_utc',
      label: 'Onboarding Slack digest — send hour (UTC)',
      help: 'UTC hour (0–23) the at-risk onboarding digest posts to Slack. The job checks hourly and sends on this hour; nothing posts on a day with nothing at risk. Use the “Send Slack digest now” button on the Deal Onboarding page for an immediate send. (UTC: e.g. 13 = 1 PM UTC.)',
      input: 'number',
      defaultHint: '13',
      validate: (v) => {
        const n = parseInt(v, 10);
        if (!Number.isFinite(n) || n < 0 || n > 23 || String(n) !== v) return 'Must be an integer 0–23';
        return null;
      },
    },
  ];
}

function fmtDatetime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState<AppSetting[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setError(null);
    try {
      const { settings: rows } = await api.admin.settings.list();
      setSettings(rows);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => { void reload(); }, []);

  if (error) return <EmptyState message={error} />;
  if (!settings) return <Spinner />;

  const byKey = new Map(settings.map((s) => [s.key, s]));

  return (
    <>
      <SectionLabel>Settings</SectionLabel>
      <div style={{ display: 'grid', gap: 10 }}>
        {KNOWN_SETTINGS.map((spec) => (
          <SettingRow
            key={spec.key}
            spec={spec}
            current={byKey.get(spec.key) ?? null}
            onSaved={reload}
          />
        ))}
        {/* HubSpot pipeline-filter settings — separate component because
            the value is a multi-selection from a dynamic list rather than
            a typed free-text input. Two independent filters:
              1. hubspot_included_pipelines — scopes the dollar series
                 (Closed Won $, Forecast $, Pipeline upside $).
              2. hubspot_deal_count_pipelines — scopes the "deals closed
                 won" count metric. Different setting because operators
                 typically want a narrow channel for the count
                 (e.g. only Implementation deals) while the dollar series
                 spans every pipeline they bill on. */}
        <HubspotPipelineSetting
          settingKey="hubspot_included_pipelines"
          title="Sales overlay — included HubSpot pipelines"
          description="Restrict the Sales overlay dollar values (Closed Won, Forecast, Pipeline upside) to deals on the selected pipelines. Leave everything unchecked to include every pipeline (no filter)."
          current={byKey.get('hubspot_included_pipelines') ?? null}
          onSaved={reload}
        />
        <HubspotPipelineSetting
          settingKey="hubspot_deal_count_pipelines"
          title="Sales overlay — pipelines for the deals closed-won count"
          description="Narrow the 'Deals closed won' count metric to deals on the selected pipelines. Independent from the filter above so the count can target a specific channel without affecting the dollar series. Empty = no filter (count every pipeline)."
          current={byKey.get('hubspot_deal_count_pipelines') ?? null}
          onSaved={reload}
        />
        <HubspotPipelineSetting
          settingKey="deal_onboarding_pipelines"
          title="Deal onboarding — included HubSpot pipelines"
          description="Only closed-won deals on the selected pipelines are added to the Deal Onboarding queue. Typically just one pipeline qualifies; the rest are handled elsewhere. Empty = no filter (every pipeline's closed-won deals are tracked)."
          current={byKey.get('deal_onboarding_pipelines') ?? null}
          onSaved={reload}
        />
        <OnboardingRequiredStepsSetting current={byKey.get('onboarding_required_steps') ?? null} onSaved={reload} />
      </div>
    </>
  );
}

/**
 * Multi-select pipeline-filter setting. Stored as comma-separated pipeline
 * IDs in app_settings — empty value means "every pipeline" (no filter).
 * UI fetches the live pipeline list from /admin/hubspot-pipelines so the
 * operator sees the actual labels + deal counts.
 *
 * Reusable across both pipeline settings (overlay-scoping and
 * deals-count-scoping) via the `settingKey` / `title` / `description` props.
 */
function HubspotPipelineSetting({
  settingKey, title, description, current, onSaved,
}: {
  settingKey: string;
  title: string;
  description: string;
  current: AppSetting | null;
  onSaved: () => void;
}) {
  const [pipelines, setPipelines] = useState<HubspotPipelineLite[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Set<string>>(() => parseIdsCsv(current?.value));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Load pipelines on mount. The hubspot_deal_pipelines table is small
  // (≤10 rows typical) so we don't need pagination or filtering here.
  useEffect(() => {
    let alive = true;
    api.admin.hubspotPipelines.list()
      .then((r) => { if (alive) setPipelines(r.pipelines); })
      .catch((e) => { if (alive) setLoadError((e as Error).message); });
    return () => { alive = false; };
  }, []);

  useEffect(() => { setDraft(parseIdsCsv(current?.value)); }, [current?.value]);

  const currentCsv = useMemo(() => Array.from(draft).sort().join(','), [draft]);
  const dirty = currentCsv !== (current?.value ?? '');

  function toggle(id: string) {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setSaved(false);
    setSaveError(null);
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await api.admin.settings.set(settingKey, currentCsv);
      setSaved(true);
      onSaved();
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <div style={{ padding: '12px 16px', display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{title}</span>
          <code style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{settingKey}</code>
          {current && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-muted)' }}>
              Last updated {fmtDatetime(current.updated_at)}
              {current.updated_by ? ` by ${current.updated_by}` : ''}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', maxWidth: 720 }}>
          {description}
        </div>
        {loadError && <Badge variant="red">Couldn't load pipelines: {loadError}</Badge>}
        {!pipelines && !loadError && <Spinner />}
        {pipelines && pipelines.length === 0 && (
          <EmptyState message="No HubSpot pipelines synced yet. Trigger a full refresh." />
        )}
        {pipelines && pipelines.length > 0 && (
          <div style={{ display: 'grid', gap: 4, marginTop: 4 }}>
            {pipelines.map((p) => {
              const checked = draft.has(p.hubspot_pipeline_id);
              return (
                <label
                  key={p.hubspot_pipeline_id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 12, cursor: 'pointer',
                    padding: '4px 6px',
                    borderRadius: 4,
                    background: checked ? 'var(--color-blue-bg)' : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(p.hubspot_pipeline_id)}
                  />
                  <span style={{ flex: 1, fontWeight: checked ? 500 : 400 }}>
                    {p.label ?? p.hubspot_pipeline_id}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                    {p.deal_count} deals · {p.open_count} open
                  </span>
                  <code style={{ fontSize: 10, color: 'var(--color-text-hint)' }}>
                    {p.hubspot_pipeline_id}
                  </code>
                </label>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
          <ActionButton
            onClick={save}
            variant="primary"
            disabled={!dirty || saving || !pipelines}
          >
            {saving ? 'Saving…' : 'Save'}
          </ActionButton>
          {saved && !dirty && <Badge variant="green">Saved</Badge>}
          {saveError && <Badge variant="red">{saveError}</Badge>}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-muted)' }}>
            {draft.size === 0
              ? 'No filter — every pipeline included.'
              : `${draft.size} pipeline${draft.size === 1 ? '' : 's'} selected.`}
          </span>
        </div>
      </div>
    </Card>
  );
}

function parseIdsCsv(s: string | null | undefined): Set<string> {
  if (!s) return new Set();
  return new Set(s.split(',').map((x) => x.trim()).filter(Boolean));
}

// ─── Onboarding required-steps matrix ────────────────────────────────────────
// Per engagement type, which "soft" onboarding steps are required. Non-required
// steps are hidden from that record's checklist + SLA. A "Default" row applies
// to types not listed (and untied records).

const REQ_STEPS: Array<{ key: string; label: string }> = [
  { key: 'sa_assigned', label: 'SA' },
  { key: 'dm_assigned', label: 'DM' },
  { key: 'handoff_doc', label: 'Handoff' },
  { key: 'welcome_email', label: 'Welcome email' },
  { key: 'pre_kickoff', label: 'Pre-KO' },
  { key: 'kickoff', label: 'KO' },
];
const REQ_DEFAULTS: Record<string, boolean> = { sa_assigned: true, dm_assigned: true, handoff_doc: true, welcome_email: true, pre_kickoff: false, kickoff: true };

interface RequiredCfg { default: Record<string, boolean>; byType: Record<string, Record<string, boolean>> }

function parseRequiredCfg(v: string | null | undefined): RequiredCfg {
  if (!v) return { default: { ...REQ_DEFAULTS }, byType: {} };
  try {
    const p = JSON.parse(v);
    return { default: { ...REQ_DEFAULTS, ...(p.default ?? {}) }, byType: (p.byType ?? {}) as Record<string, Record<string, boolean>> };
  } catch {
    return { default: { ...REQ_DEFAULTS }, byType: {} };
  }
}

function OnboardingRequiredStepsSetting({ current, onSaved }: { current: AppSetting | null; onSaved: () => void }) {
  const [types, setTypes] = useState<string[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cfg, setCfg] = useState<RequiredCfg>(() => parseRequiredCfg(current?.value));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.dealOnboarding.engagementTypes()
      .then((r) => { if (alive) setTypes(r.types); })
      .catch((e) => { if (alive) setLoadError((e as Error).message); });
    return () => { alive = false; };
  }, []);
  useEffect(() => { setCfg(parseRequiredCfg(current?.value)); }, [current?.value]);

  const effective = (type: string | null, step: string): boolean => {
    if (type === null) return cfg.default[step] ?? REQ_DEFAULTS[step];
    return cfg.byType[type]?.[step] ?? cfg.default[step] ?? REQ_DEFAULTS[step];
  };
  function toggle(type: string | null, step: string) {
    setCfg((prev) => {
      const next: RequiredCfg = { default: { ...prev.default }, byType: { ...prev.byType } };
      const cur = type === null ? (prev.default[step] ?? REQ_DEFAULTS[step]) : (prev.byType[type]?.[step] ?? prev.default[step] ?? REQ_DEFAULTS[step]);
      if (type === null) next.default[step] = !cur;
      else next.byType[type] = { ...(next.byType[type] ?? {}), [step]: !cur };
      return next;
    });
    setSaved(false); setSaveError(null);
  }
  async function save() {
    setSaving(true); setSaveError(null); setSaved(false);
    try {
      await api.admin.settings.set('onboarding_required_steps', JSON.stringify(cfg));
      setSaved(true); onSaved();
    } catch (e) { setSaveError((e as Error).message); } finally { setSaving(false); }
  }

  const Row = ({ type, label }: { type: string | null; label: string }) => (
    <Tr>
      <Td bold>{label}</Td>
      {REQ_STEPS.map((s) => (
        <Td key={s.key}>
          <input type="checkbox" checked={effective(type, s.key)} onChange={() => toggle(type, s.key)} />
        </Td>
      ))}
    </Tr>
  );

  return (
    <Card>
      <div style={{ padding: '12px 16px', display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Deal onboarding — required steps per engagement type</span>
          <code style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>onboarding_required_steps</code>
          {current && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-muted)' }}>
              Last updated {fmtDatetime(current.updated_at)}{current.updated_by ? ` by ${current.updated_by}` : ''}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', maxWidth: 720 }}>
          Tick the steps each engagement type requires. Unticked steps are hidden from that record's checklist and SLA (and excluded from cycle-time metrics). The <strong>Default</strong> row applies to types not listed below and to records whose engagement isn't tied yet. Invoice issued/paid and engagement-created are always required.
        </div>
        {loadError && <Badge variant="red">Couldn't load engagement types: {loadError}</Badge>}
        {!types && !loadError && <Spinner />}
        {types && (
          <Table>
            <thead><tr><Th>Engagement type</Th>{REQ_STEPS.map((s) => <Th key={s.key}>{s.label}</Th>)}</tr></thead>
            <tbody>
              <Row type={null} label="Default (all other types)" />
              {types.length === 0
                ? <Tr><Td muted>No engagement types synced yet.</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td></Tr>
                : types.map((t) => <Row key={t} type={t} label={t} />)}
            </tbody>
          </Table>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
          <ActionButton onClick={save} variant="primary" disabled={saving || !types}>{saving ? 'Saving…' : 'Save'}</ActionButton>
          {saved && <Badge variant="green">Saved</Badge>}
          {saveError && <Badge variant="red">{saveError}</Badge>}
        </div>
      </div>
    </Card>
  );
}

function SettingRow({
  spec, current, onSaved,
}: {
  spec: KnownSetting;
  current: AppSetting | null;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(current?.value ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setDraft(current?.value ?? ''); }, [current?.value]);

  const dirty = draft !== (current?.value ?? '');
  const validation = draft ? spec.validate(draft) : 'Required';

  async function save() {
    if (validation) { setSaveError(validation); return; }
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await api.admin.settings.set(spec.key, draft);
      setSaved(true);
      onSaved();
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <div style={{ padding: '12px 16px', display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{spec.label}</span>
          <code style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{spec.key}</code>
          {current && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-muted)' }}>
              Last updated {fmtDatetime(current.updated_at)}
              {current.updated_by ? ` by ${current.updated_by}` : ''}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', maxWidth: 700 }}>
          {spec.help}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type={spec.input}
            value={draft}
            placeholder={spec.defaultHint}
            onChange={(e) => { setDraft(e.target.value); setSaved(false); setSaveError(null); }}
            style={{
              fontSize: 12, padding: '4px 8px', width: 220,
              border: '0.5px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface)', color: 'var(--color-text)',
            }}
          />
          <ActionButton
            onClick={save}
            variant="primary"
            disabled={!dirty || !!validation || saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </ActionButton>
          {saved && !dirty && <Badge variant="green">Saved</Badge>}
          {saveError && <Badge variant="red">{saveError}</Badge>}
        </div>
      </div>
    </Card>
  );
}
